"use client";

import {
  initializeApp,
  getApps,
  getApp,
  type FirebaseApp,
} from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getDatabase, type Database } from "firebase/database";
import { getAnalytics, type Analytics, isSupported } from "firebase/analytics";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { getFirestore, type Firestore } from "firebase/firestore";
import { firebaseConfig } from "@/config/APP/firebase.config";

let app: FirebaseApp;
let auth: Auth;
let database: Database;
let firestore: Firestore;
let storage: FirebaseStorage;
let analytics: Analytics | null = null;

function getFirebaseApp(): FirebaseApp {
  if (typeof window === "undefined") {
    throw new Error("Firebase is only available on the client.");
  }
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApp();
  }
  return app;
}

/** Use in client components only. Returns the Firebase App (singleton). */
export function getFirebase(): FirebaseApp {
  return getFirebaseApp();
}

/** Firebase Auth. Use in client components only. */
export function getFirebaseAuth(): Auth {
  if (typeof window === "undefined") {
    throw new Error("Firebase Auth is only available on the client.");
  }
  if (!auth) {
    const firebaseApp = getFirebaseApp();
    auth = getAuth(firebaseApp);
  }
  return auth;
}

/** Realtime Database. Use in client components only. */
export function getFirebaseDatabase(): Database {
  if (typeof window === "undefined") {
    throw new Error("Firebase Database is only available on the client.");
  }
  if (!database) {
    const firebaseApp = getFirebaseApp();
    database = getDatabase(firebaseApp);
  }
  return database;
}

/** Firestore. Use in client components only. */
export function getFirebaseFirestore(): Firestore {
  if (typeof window === "undefined") {
    throw new Error("Firebase Firestore is only available on the client.");
  }
  if (!firestore) {
    const firebaseApp = getFirebaseApp();
    firestore = getFirestore(firebaseApp);
  }
  return firestore;
}

/** Cloud Storage. Use in client components only. */
export function getFirebaseStorage(): FirebaseStorage {
  if (typeof window === "undefined") {
    throw new Error("Firebase Storage is only available on the client.");
  }
  if (!storage) {
    const firebaseApp = getFirebaseApp();
    storage = getStorage(firebaseApp);
  }
  return storage;
}

/**
 * Firebase Analytics (optional). Resolves to Analytics or null if unsupported (e.g. in Node).
 * Use in client components: getFirebaseAnalytics() then await when needed.
 */
export async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (typeof window === "undefined") return null;
  if (analytics != null) return analytics;
  const supported = await isSupported();
  if (!supported) return null;
  const firebaseApp = getFirebaseApp();
  analytics = getAnalytics(firebaseApp);
  return analytics;
}

/** Synchronous getter for Analytics if already initialized (e.g. after app load). */
export function getFirebaseAnalyticsSync(): Analytics | null {
  return analytics ?? null;
}
